// client/src/services/api.js

import { API_CONFIG } from '../config/api';
import { getCurrentTimestamp } from '../utils/dateUtils';

const API_URL = API_CONFIG.API_URL;


// Authentication
export const login = async (credentials) => {
  try {
    const response = await fetch(`${API_URL}/api/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(credentials)
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('Login error:', errorData);
      throw new Error(errorData.message || `Login failed with status: ${response.status}`);
    }
    
    return response.json();
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
};

// Customer Search
export const searchCustomers = async (searchTerm) => {
  try {
    if (!searchTerm || searchTerm.length < 2) {
      return [];
    }
    
    console.log('Searching for customers with term:', searchTerm);
    
    const response = await fetch(`${API_URL}/customers/search?term=${encodeURIComponent(searchTerm)}`);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('Customer search error:', errorData);
      throw new Error(errorData.message || `Search failed with status: ${response.status}`);
    }
    
    const data = await response.json();
    console.log('Found customers:', Array.isArray(data) ? data.length : 'invalid data');
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error('Error searching customers:', error);
    throw error;
  }
};

// Get customer order history
export const getCustomerOrders = async (customerId) => {
  try {
    const response = await fetch(`${API_URL}/customers/${customerId}/orders`);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to fetch orders with status: ${response.status}`);
    }
    
    return response.json();
  } catch (error) {
    console.error('Error fetching customer orders:', error);
    throw error;
  }
};



// Orders
export const getOrders = async (searchTerm = '') => {
  try {
    const response = await fetch(`${API_URL}/orders?search=${encodeURIComponent(searchTerm)}`);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to fetch orders with status: ${response.status}`);
    }
    
    return response.json();
  } catch (error) {
    console.error('Error fetching orders:', error);
    throw error;
  }
};

// Get order by number
export const getOrderByNumber = async (orderNo) => {
  try {
    const response = await fetch(`${API_URL}/orders/order/${orderNo}`);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to fetch order with status: ${response.status}`);
    }
    
    return response.json();
  } catch (error) {
    console.error(`Error fetching order ${orderNo}:`, error);
    throw error;
  }
};

// Get repair options
export const getRepairOptions = async () => {
  try {
    const response = await fetch(`${API_URL}/get-repair-options`);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to fetch repair options with status: ${response.status}`);
    }
    
    return response.json();
  } catch (error) {
    console.error('Error fetching repair options:', error);
    throw error;
  }
};

// Get accessories
export const getAccessories = async () => {
  try {
    const response = await fetch(`${API_URL}/get-accessories`);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to fetch accessories with status: ${response.status}`);
    }
    
    return response.json();
  } catch (error) {
    console.error('Error fetching accessories:', error);
    throw error;
  }
};



// Get tasks for a room
export const getRoomTasks = async (roomNo) => {
  try {
    const response = await fetch(`${API_URL}/get-room-tasks/${roomNo}`);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to fetch room tasks with status: ${response.status}`);
    }
    
    return response.json();
  } catch (error) {
    console.error(`Error fetching tasks for room ${roomNo}:`, error);
    throw error;
  }
};

// Get workers for a room
export const getRoomWorkers = async (roomNo) => {
  try {
    const response = await fetch(`${API_URL}/get-room-workers/${roomNo}`);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to fetch room workers with status: ${response.status}`);
    }
    
    return response.json();
  } catch (error) {
    console.error(`Error fetching workers for room ${roomNo}:`, error);
    throw error;
  }
};






// Agreement Management
export const sendAgreementSMS = async (agreementData) => {
  try {
    const response = await fetch(`${API_URL}/api/agreements/send-sms`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(agreementData)
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to send agreement SMS with status: ${response.status}`);
    }
    
    return response.json();
  } catch (error) {
    console.error('Error sending agreement SMS:', error);
    throw error;
  }
};



export const sendAgreementReminder = async (reminderData) => {
  try {
    const response = await fetch(`${API_URL}/api/agreements/reminder`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(reminderData)
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to send reminder with status: ${response.status}`);
    }
    
    return response.json();
  } catch (error) {
    console.error('Error sending agreement reminder:', error);
    throw error;
  }
};



// Get available workers
export const getAvailableWorkers = async () => {
  try {
    const response = await fetch('/api/workers', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error getting available workers:', error);
    throw error;
  }
};

// ========== MANUFACTURING-SPECIFIC API FUNCTIONS ==========
export const authenticatePin = async (pinCode, roomNo = null) => {
  try {
    const response = await fetch(`${API_URL}/api/verify-user-pin`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ 
        pinCode: pinCode,
        roomNo: roomNo 
      })
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `PIN authentication failed with status: ${response.status}`);
    }
    
    return response.json();
  } catch (error) {
    console.error('PIN authentication error:', error);
    throw error;
  }
};



// Get room inventory with filters
export const getRoomInventory = async (roomNo, filters = {}) => {
  try {
    const params = new URLSearchParams();
    if (filters.barcode) params.append('barcode', filters.barcode);
    if (filters.serialCode) params.append('serialCode', filters.serialCode);
    if (filters.worker) params.append('worker', filters.worker);
    
    const queryString = params.toString();
    const url = queryString 
      ? `${API_URL}/api/room-inventory/${roomNo}?${queryString}`
      : `${API_URL}/api/room-inventory/${roomNo}`;
    
    const response = await fetch(url);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to fetch room inventory with status: ${response.status}`);
    }
    
    return response.json();
  } catch (error) {
    console.error(`Error fetching room inventory for room ${roomNo}:`, error);
    throw error;
  }
};

// Find barcode in specific room
export const findBarcodeInRoom = async (barcode, roomNo, serialCode = null) => {
  try {
    const payload = { barcode, roomNo };
    if (serialCode) payload.serialCode = serialCode;
    
    const response = await fetch(`${API_URL}/api/find-barcode-room`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Barcode search failed with status: ${response.status}`);
    }
    
    return response.json();
  } catch (error) {
    console.error('Barcode search error:', error);
    throw error;
  }
};

// ========== TASK MANAGEMENT ==========

// Start work on a task
export const startWorkTask = async (taskId, workerId, roomNo) => {
  try {
    const response = await fetch(`${API_URL}/api/tasks/start-work`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ taskId, workerId, roomNo })
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to start work with status: ${response.status}`);
    }
    
    return response.json();
  } catch (error) {
    console.error('Start work error:', error);
    throw error;
  }
};

// Finish work on a task
export const finishWorkTask = async (taskId, workerId, roomNo) => {
  try {
    const response = await fetch(`${API_URL}/api/tasks/finish-work`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ taskId, workerId, roomNo })
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to finish work with status: ${response.status}`);
    }
    
    return response.json();
  } catch (error) {
    console.error('Finish work error:', error);
    throw error;
  }
};

// Pause task (hold/lunch/go home)
export const pauseWorkTask = async (taskId, reason, workerId) => {
  try {
    const response = await fetch(`${API_URL}/api/tasks/pause`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ taskId, reason, workerId })
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to pause task with status: ${response.status}`);
    }
    
    return response.json();
  } catch (error) {
    console.error('Pause task error:', error);
    throw error;
  }
};

// Resume paused task
export const resumeWorkTask = async (taskId, workerId) => {
  try {
    const response = await fetch(`${API_URL}/api/tasks/resume`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ taskId, workerId })
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `Failed to resume task with status: ${response.status}`);
    }
    
    return response.json();
  } catch (error) {
    console.error('Resume task error:', error);
    throw error;
  }
};



// ========== CONVENIENCE FUNCTIONS ==========
// These wrap the existing functions with easier names

// Lunch break (wrapper for pauseWorkTask)
export const takeLunchBreak = async (taskId, workerId) => {
  return pauseWorkTask(taskId, 'Lunch Break', workerId);
};

// Go home (wrapper for pauseWorkTask)
export const goHomeFromWork = async (taskId, workerId) => {
  return pauseWorkTask(taskId, 'Going home', workerId);
};

// Hold task with custom reason (wrapper for pauseWorkTask)
export const holdWorkTask = async (taskId, reason, workerId) => {
  return pauseWorkTask(taskId, reason, workerId);
};

// ========== ERROR HANDLING HELPER ==========
// Reusable error handler for manufacturing operations
export const handleManufacturingError = (error, context = '') => {
  const errorMessage = error.message || 'An unexpected error occurred';
  const fullMessage = context ? `${context}: ${errorMessage}` : errorMessage;
  
  console.error('Manufacturing API Error:', {
    context,
    error: errorMessage,
    timestamp: getCurrentTimestamp(),
  });
  
  // User-friendly error mapping
  const userFriendlyErrors = {
    'Network Error': 'Unable to connect to server. Please check your internet connection.',
    'Failed to fetch': 'Server is unavailable. Please try again later.',
    'Unauthorized': 'Your session has expired. Please log in again.',
    'Forbidden': 'You do not have permission to perform this action.',
    'Not Found': 'The requested resource was not found.',
    'PIN authentication failed': 'Invalid PIN code. Please try again.',
    'Barcode not found': 'This barcode was not found in the current room.',
    'Task not found': 'The selected task could not be found.',
    'Transfer validation failed': 'This transfer is not allowed. Please check the room workflow.',
  };
  
  return userFriendlyErrors[errorMessage] || fullMessage;
};

